/**
 * PC比較リストページの描画・操作
 * - CompareStore から読み込み
 * - カードを描画、削除、カート投入
 * - レビューデータから星評価を計算
 */
(function () {
  'use strict';

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function createEl(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text != null) el.textContent = text;
    return el;
  }

  function formatYen(cents) {
    if (!cents) return '¥—';
    const yen = Math.round(cents / 100);
    return '¥' + yen.toLocaleString('ja-JP');
  }

  function formatGpu(raw) {
    if (!raw) return '';
    const lower = raw.toLowerCase();
    // 既にブランド名が含まれている場合は整形済みとしてそのまま返す
    if (lower.indexOf('nvidia') >= 0 || lower.indexOf('geforce') >= 0 || lower.indexOf('radeon') >= 0) {
      return raw;
    }
    let brand = '';
    if (lower.indexOf('rtx') >= 0 || lower.indexOf('gtx') >= 0) brand = 'NVIDIA GeForce ';
    else if (lower.indexOf('rx') >= 0) brand = 'AMD Radeon ';
    let fmt = raw
      .replace(/RTX/g, 'RTX ')
      .replace(/GTX/g, 'GTX ')
      .replace(/RX/g, 'RX ')
      .replace(/ti/g, 'Ti')
      .replace(/TI/g, 'Ti')
      .replace(/Ti/g, ' Ti')
      .replace(/XT/g, ' XT')
      .replace(/\s+/g, ' ')
      .trim();
    return (brand + fmt).trim();
  }

  function getRatingForSku(sku) {
    if (!sku) return null;
    const upperSku = String(sku).toUpperCase();
    // 軽量集計マップ（product-reviews-summary.js、全ページ読込）を優先
    const summary = window.__smoReviewsSummary;
    if (summary && summary[upperSku]) {
      return { avg: Number(summary[upperSku].avg) || 0, count: Number(summary[upperSku].count) || 0 };
    }
    // フォールバック: フルデータ（product-reviews-data.js）が読み込まれている場合
    if (!window.__smoReviews) return null;
    const reviews = window.__smoReviews.filter((r) => (r.sku || '').toUpperCase() === upperSku);
    if (reviews.length === 0) return null;
    const sum = reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0);
    return { avg: sum / reviews.length, count: reviews.length };
  }

  function buildStars(avg) {
    const full = Math.floor(avg);
    const hasHalf = avg - full >= 0.25 && avg - full < 0.75;
    const hasUpper = avg - full >= 0.75;
    let stars = '';
    for (let i = 1; i <= 5; i++) {
      if (i <= full || (i === full + 1 && hasUpper)) stars += '\u2605';
      else if (i === full + 1 && hasHalf) stars += '\u2B50';
      else stars += '\u2606';
    }
    return stars;
  }

  function buildSpecRow(key, value) {
    const li = document.createElement('li');
    li.appendChild(createEl('span', 'compare-card__specs-key', key));
    li.appendChild(createEl('span', 'compare-card__specs-val', String(value || '—')));
    return li;
  }

  // 現在の商品画像を取り直して img に反映し、比較リストの保存値も更新する (次回以降の 404 を防ぐ)
  const imageCache = {};
  function refreshImage(item, img) {
    if (!item.handle) return;
    const p = imageCache[item.handle] || (imageCache[item.handle] = fetch('/products/' + encodeURIComponent(item.handle) + '.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((product) => {
        const src = product && (product.featured_image || (product.images && product.images[0]));
        if (!src) return '';
        return src.indexOf('?') >= 0 ? src + '&width=400' : src + '?width=400';
      })
      .catch(() => ''));
    p.then((src) => {
      if (!src || src === img.src) return;
      img.src = src;
      item.imageSrc = src;
      if (window.CompareStore && typeof window.CompareStore.updateItem === 'function') {
        window.CompareStore.updateItem(item.id, { imageSrc: src }).catch(() => {});
      }
    });
  }

  function buildCard(item, onRemove, onAddToCart) {
    const card = createEl('article', 'compare-card');
    card.dataset.id = item.id;

    // 削除ボタン
    const removeBtn = createEl('button', 'compare-card__remove', '×');
    removeBtn.type = 'button';
    removeBtn.setAttribute('aria-label', '比較リストから削除');
    removeBtn.addEventListener('click', () => onRemove(item.id));
    card.appendChild(removeBtn);

    // 画像（小さく）
    // 保存済みの imageSrc は追加時点の CDN URL のスナップショット。pc-manager の Shopify 同期 (CSV 画像行) で
    // 画像が再取込されると uuid 付き URL が変わり 404 になるため、読めなければ /products/<handle>.js の
    // 現在の featured_image に差し替える (imageSrc 未保存の古いデータも同じ経路で補う)
    if (item.imageSrc || item.handle) {
      const imgWrap = createEl('div', 'compare-card__image');
      const img = document.createElement('img');
      img.alt = item.title || '';
      img.loading = 'lazy';
      img.addEventListener('error', () => refreshImage(item, img), { once: true });
      if (item.imageSrc) img.src = item.imageSrc;
      else refreshImage(item, img);
      imgWrap.appendChild(img);
      card.appendChild(imgWrap);
    }

    // タイトル
    const titleEl = createEl('h2', 'compare-card__title');
    const titleLink = document.createElement('a');
    titleLink.href = '/products/' + item.handle;
    titleLink.textContent = item.title || '';
    titleEl.appendChild(titleLink);
    card.appendChild(titleEl);

    // 星評価（SKU優先。旧アイテムはhandleがSKUと同名規則(g00324→G00324)のためフォールバック）
    const ratingData = getRatingForSku(item.sku || item.handle);
    if (ratingData) {
      const ratingEl = createEl('div', 'compare-card__rating');
      ratingEl.appendChild(createEl('span', 'compare-card__rating-stars', buildStars(ratingData.avg)));
      ratingEl.appendChild(createEl('span', 'compare-card__rating-score', ratingData.avg.toFixed(1)));
      ratingEl.appendChild(createEl('span', 'compare-card__rating-count', '(' + ratingData.count + ')'));
      card.appendChild(ratingEl);
    }

    // 価格
    const priceEl = createEl('div', 'compare-card__price');
    priceEl.textContent = formatYen(item.totalPrice || item.basePrice);
    const small = createEl('small', null, ' (税込・オプション込)');
    priceEl.appendChild(small);
    card.appendChild(priceEl);

    // 基本仕様表（CPU/GPU/マザーボード/保証のみ、他は「選択オプション」で表示）
    const specSection = createEl('div', 'compare-card__section');
    specSection.appendChild(createEl('h3', 'compare-card__section-title', '基本仕様'));
    const specList = createEl('ul', 'compare-card__specs');
    specList.appendChild(buildSpecRow('CPU', item.cpu));
    specList.appendChild(buildSpecRow('GPU', formatGpu(item.gpu) || item.gpu));
    if (item.motherboard) {
      specList.appendChild(buildSpecRow('マザーボード', item.motherboard));
    }
    if (item.warranty) {
      specList.appendChild(buildSpecRow('保証', item.warranty));
    }
    specSection.appendChild(specList);
    card.appendChild(specSection);

    // 選択オプション一覧（カスタマイズ UI で選んだもののみ）
    if (item.properties && Object.keys(item.properties).length > 0) {
      const optSection = createEl('div', 'compare-card__section');
      optSection.appendChild(createEl('h3', 'compare-card__section-title', '選択オプション'));
      const optList = createEl('ul', 'compare-card__specs');
      Object.entries(item.properties).forEach(([k, v]) => {
        // 内部用キー（先頭 _。_smolab_* 等）は非表示
        if (k.startsWith('_')) return;
        optList.appendChild(buildSpecRow(k, v));
      });
      if (optList.children.length > 0) {
        optSection.appendChild(optList);
        card.appendChild(optSection);
      }
    }

    // カートに追加
    const cartBtn = createEl('button', 'compare-card__cart-button', 'カートに追加');
    cartBtn.type = 'button';
    cartBtn.addEventListener('click', () => onAddToCart(item, cartBtn));
    card.appendChild(cartBtn);

    return card;
  }

  function filterGItems(items) {
    // 念のため: Gつき商品のみを返す（handle が g で始まる、または handle で判定）
    return items.filter((it) => {
      if (!it.handle) return false;
      return String(it.handle).toLowerCase().startsWith('g');
    });
  }

  async function render() {
    if (!window.CompareStore) {
      console.error('[compare-page] CompareStore not loaded');
      return;
    }
    const grid = $('[data-compare-grid]');
    const emptyEl = $('[data-compare-empty]');
    const actionsEl = $('[data-compare-actions]');
    if (!grid) return;

    const state = await window.CompareStore.load();
    const items = filterGItems(state.items || []);

    // grid クリア
    while (grid.firstChild) grid.removeChild(grid.firstChild);

    if (items.length === 0) {
      if (emptyEl) emptyEl.hidden = false;
      if (actionsEl) actionsEl.hidden = true;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    if (actionsEl) actionsEl.hidden = false;

    items.forEach((item) => {
      const card = buildCard(item, handleRemove, handleAddToCart);
      grid.appendChild(card);
    });
  }

  async function handleRemove(id) {
    if (!window.CompareStore) return;
    if (!confirm('比較リストから削除しますか？')) return;
    await window.CompareStore.remove(id);
    render();
  }

  async function handleClear() {
    if (!window.CompareStore) return;
    if (!confirm('全ての比較リストをクリアしますか？')) return;
    await window.CompareStore.clear();
    render();
  }

  /**
   * カート追加: 商品ページに遷移してカスタマイズ UI に保存値を復元 → submit。
   * 商品ページ経由でないと正しいカート構成（本体 + オプション line items）にならない。
   */
  async function handleAddToCart(item, btn) {
    btn.disabled = true;
    btn.textContent = '商品ページへ移動中...';
    var url = '/products/' + item.handle + '?_cmp_add=' + encodeURIComponent(item.id);
    window.location.href = url;
  }

  function init() {
    render();
    const clearBtn = $('[data-compare-clear]');
    if (clearBtn) clearBtn.addEventListener('click', handleClear);
    window.addEventListener('smolab:compare:change', render);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
